import { useState } from 'react';
import { 
  FieldType, 
  getCharacterLimit, 
  validateCharacterLimit, 
  getLanguageAdjustedLimit 
} from '../lib/ai/characterLimits';

interface UseAIGenerationOptions {
  onSuccess?: (text: string) => void;
  onError?: (error: string) => void;
}

interface GenerateOptions {
  prompt: string;
  context?: string;
  fieldType?: FieldType | 'title' | 'description' | 'sellingPoint' | 'feature' | 'default'; // Backward compatibility
  language?: string;
  maxRetries?: number;
  validateCharacterLimits?: boolean;
}

export const useAIGeneration = (options: UseAIGenerationOptions = {}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async ({ 
    prompt, 
    context, 
    fieldType = 'default',
    language = 'en',
    maxRetries = 2,
    validateCharacterLimits = true
  }: GenerateOptions) => {
    if (!prompt.trim()) {
      const errorMsg = 'Please provide a prompt for AI generation';
      setError(errorMsg);
      options.onError?.(errorMsg);
      return null;
    }

    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (!apiKey) {
      const errorMsg = 'Gemini API key not configured. Please add REACT_APP_GEMINI_API_KEY to your environment variables.';
      setError(errorMsg);
      options.onError?.(errorMsg);
      return null;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Map legacy field types to new FieldType enum for backward compatibility
      const fieldTypeMapping: Record<string, FieldType> = {
        'title': FieldType.HERO_TITLE,
        'description': FieldType.PROJECT_DESCRIPTION,
        'sellingPoint': FieldType.SELLING_POINT_TITLE,
        'feature': FieldType.FEATURE_TITLE,
        'default': FieldType.PROJECT_DESCRIPTION
      };

      const mappedFieldType = typeof fieldType === 'string' && fieldType in fieldTypeMapping 
        ? fieldTypeMapping[fieldType] 
        : fieldType as FieldType;

      // Get character limit for the field type and language
      let characterLimit: number | null = null;
      if (validateCharacterLimits && fieldType !== 'default') {
        characterLimit = getLanguageAdjustedLimit(mappedFieldType, language);
      }

      // Enhanced system prompts with character limit awareness
      const systemPrompts = {
        [FieldType.HERO_TITLE]: `You are a real estate marketing expert. Generate compelling, professional property titles that are concise and attractive. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Return only the title text, no quotes or extra formatting.`,
        
        [FieldType.HERO_SUBTITLE]: `You are a real estate marketing expert. Generate engaging property subtitles that complement the main title. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Be descriptive but concise.`,
        
        [FieldType.HERO_HIGHLIGHT]: `You are a real estate marketing expert. Generate a compelling property highlight point. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Be punchy and benefit-focused.`,
        
        [FieldType.PROJECT_DESCRIPTION]: `You are a real estate marketing expert. Generate engaging property descriptions that highlight key features and benefits. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Keep it professional and appealing to potential buyers/investors.`,
        
        [FieldType.SELLING_POINT_TITLE]: `You are a real estate marketing expert. Generate a compelling selling point headline for a property. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Return only the selling point text, be concise and impactful.`,
        
        [FieldType.SELLING_POINT_DESCRIPTION]: `You are a real estate marketing expert. Generate a detailed selling point description. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Explain the benefit and value to potential buyers.`,
        
        [FieldType.FEATURE_TITLE]: `You are a real estate marketing expert. Generate a property feature or amenity name. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Be specific and appealing.`,
        
        [FieldType.FEATURE_DESCRIPTION]: `You are a real estate marketing expert. Generate a property feature description. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Be specific and highlight the benefit to residents.`,
        
        [FieldType.FAQ_QUESTION]: `You are a real estate marketing expert. Generate a frequently asked question about the property. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Make it clear and relevant to potential buyers.`,
        
        [FieldType.FAQ_ANSWER]: `You are a real estate marketing expert. Generate a comprehensive answer to a property FAQ. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Be informative and reassuring.`,
        
        [FieldType.PAYMENT_PLAN_NAME]: `You are a real estate marketing expert. Generate an attractive payment plan name. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Make it appealing and easy to understand.`,
        
        [FieldType.LOCATION_BENEFIT]: `You are a real estate marketing expert. Generate a location benefit description. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Highlight connectivity, convenience, or prestige.`,
        
        // Legacy compatibility
        'title': `You are a real estate marketing expert. Generate compelling, professional property titles that are concise and attractive. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Return only the title text, no quotes or extra formatting.`,
        'description': `You are a real estate marketing expert. Generate engaging property descriptions that highlight key features and benefits. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Keep it professional and appealing to potential buyers/investors.`,
        'sellingPoint': `You are a real estate marketing expert. Generate a compelling selling point for a property. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Return only the selling point text, be concise and impactful.`,
        'feature': `You are a real estate marketing expert. Generate a property feature description. ${characterLimit ? `Keep it under ${characterLimit} characters.` : ''} Be specific and highlight the benefit to residents.`,
        'default': `You are a helpful assistant that generates real estate marketing content. ${characterLimit ? `Keep responses under ${characterLimit} characters.` : ''} Be professional, accurate, and engaging.`
      };

      const systemPrompt = systemPrompts[fieldType] || systemPrompts['default'];

      // Enhanced prompt construction with language awareness
      let fullPrompt = '';
      if (language !== 'en') {
        fullPrompt += `Please respond in ${language === 'ar' ? 'Arabic' : language === 'fa' ? 'Persian' : language === 'ur' ? 'Urdu' : 'the target language'}. `;
      }
      
      if (context) {
        fullPrompt += `Project Context: ${context}\n\n`;
      }
      
      fullPrompt += `Generate: ${prompt}`;
      
      if (characterLimit) {
        fullPrompt += `\n\nIMPORTANT: Keep the response under ${characterLimit} characters.`;
      }

      let attempts = 0;
      let generatedText: string | null = null;

      // Retry logic for character limit compliance
      while (attempts <= maxRetries) {
        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: `${systemPrompt}\n\nPrompt: ${fullPrompt}`
              }]
            }],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 2048,
            }
          }),
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`Gemini API Error: ${response.status} - ${errorData}`);
        }

        const data = await response.json();
        
        if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
          throw new Error('Invalid response from Gemini API');
        }

        const content = data.candidates[0].content.parts[0].text;

        if (!content) {
          throw new Error('No content generated');
        }

        generatedText = content.trim();

        // Validate character limits if enabled
        if (validateCharacterLimits && characterLimit && fieldType !== 'default' && generatedText) {
          const validation = validateCharacterLimit(generatedText, mappedFieldType);
          
          if (validation.isValid) {
            break; // Success - within character limits
          } else if (attempts < maxRetries) {
            // Retry with stricter prompt
            attempts++;
            fullPrompt = fullPrompt.replace(
              `Keep the response under ${characterLimit} characters.`,
              `CRITICAL: Response must be EXACTLY under ${characterLimit} characters. Current attempt exceeded by ${validation.exceedsBy} characters. Make it much shorter.`
            );
            continue;
          } else {
            // Final attempt failed - provide warning but return content
            console.warn(`Generated content exceeds character limit by ${validation.exceedsBy} characters for field type ${mappedFieldType}`);
            // Truncate to fit if possible
            if (validation.exceedsBy < characterLimit * 0.1) { // Only if close to limit
              generatedText = content.substring(0, characterLimit - 3) + '...';
            }
            break;
          }
        } else {
          generatedText = content.trim();
          break; // No validation needed
        }
      }

      if (!generatedText) {
        throw new Error('Failed to generate valid content within character limits');
      }

      options.onSuccess?.(generatedText);
      return generatedText;

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMsg);
      options.onError?.(errorMsg);
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generate,
    isGenerating,
    error,
    clearError: () => setError(null),
  };
}; 